import { useEffect, useMemo, useState } from "react";
import type { ChartOptions } from "chart.js";
import { normalizeColor, withAlpha } from "@/charts/utils";

/*
 * Chart theme adapter (DESIGN_GUIDE §4 + §8a).
 *
 * Reads the active CSS-variable tokens at runtime and re-runs whenever
 * the page theme (data-theme) or brand (data-brand) changes. Returns:
 *   - `tokens`: resolved colour / font values, for hand-rolled chart components
 *   - `options`: a base Chart.js ChartOptions object that consumes those tokens
 *   - `palette`: an ordered list of series colours (focal accent first)
 *
 * Charts deep-merge `options` with their type-specific overrides.
 */

export interface ChartTokens {
  fg: string;
  fgMuted: string;
  fgSubtle: string;
  surface: string;
  canvas: string;
  border: string;
  borderStrong: string;
  accent: string;
  accentSubtle: string;
  accentText: string;
  /** Brand-controlled focal data-ink colour (may differ from CTA accent). */
  chartFocal: string;
  success: string;
  warning: string;
  danger: string;
  info: string;
  fontSans: string;
  fontMono: string;
}

function readTokens(): ChartTokens {
  const css = getComputedStyle(document.documentElement);
  // Normalize through the canvas — converts oklch() / lab() / etc. into
  // the sRGB hex/rgb form that @kurkle/color (chart.js' parser) accepts.
  // Without this, Sankey's `applyAlpha` crashes on modern colour spaces.
  const v = (name: string) => normalizeColor(css.getPropertyValue(name).trim());
  // Fonts aren't colour values, read raw.
  const raw = (name: string) => css.getPropertyValue(name).trim();
  const accent = v("--accent-bg");
  return {
    fg:           v("--text-default"),
    fgMuted:      v("--text-muted"),
    fgSubtle:     v("--text-subtle"),
    surface:      v("--bg-surface"),
    canvas:       v("--bg-canvas"),
    border:       v("--border-subtle"),
    borderStrong: v("--border-default"),
    accent:       accent,
    accentSubtle: v("--accent-bg-subtle"),
    accentText:   v("--accent-text"),
    chartFocal:   v("--chart-focal") || accent,
    success:      v("--success-text"),
    warning:      v("--warning-text"),
    danger:       v("--danger-text"),
    info:         v("--info-text"),
    fontSans:     raw("--font-sans"),
    fontMono:     raw("--font-mono"),
  };
}

/** Returns a chart theme that updates when [data-theme] or [data-brand] changes. */
export function useChartTheme() {
  const [tokens, setTokens] = useState<ChartTokens>(() => readTokens());

  useEffect(() => {
    // Re-read tokens whenever theme or brand attributes change on <html>.
    const obs = new MutationObserver(() => setTokens(readTokens()));
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme", "data-brand"],
    });
    return () => obs.disconnect();
  }, []);

  const options = useMemo<ChartOptions>(() => buildBaseOptions(tokens), [tokens]);
  const palette = useMemo(() => buildPalette(tokens), [tokens]);

  return { tokens, options, palette };
}

function buildBaseOptions(t: ChartTokens): ChartOptions {
  // DESIGN_GUIDE §4: "muted palette, gridlines low-contrast."
  // Both gridlines and the dropped axis lines are intentionally faint —
  // content reads first, scale reads second.
  const gridColor = withAlpha(t.border, 0.4);
  return {
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: typeof window !== "undefined" ? window.devicePixelRatio : 1,
    interaction: {
      mode: "index",
      intersect: false,
    },
    layout: { padding: { top: 4, right: 4 } },
    plugins: {
      legend: {
        display: false,
        position: "bottom",
        labels: {
          color: t.fgMuted,
          font: { family: t.fontSans, size: 12 },
          usePointStyle: true,
          boxWidth: 6,
          boxHeight: 6,
          padding: 16,
        },
      },
      tooltip: {
        backgroundColor: t.fg,
        titleColor: t.surface,
        bodyColor: t.surface,
        borderColor: "transparent",
        borderWidth: 0,
        padding: 10,
        cornerRadius: 8,
        displayColors: true,
        boxPadding: 6,
        usePointStyle: true,
        caretSize: 5,
        titleFont: { family: t.fontSans, weight: 600, size: 12 },
        bodyFont:  { family: t.fontMono, weight: 500, size: 12 },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          color: t.fgSubtle,
          font: { family: t.fontSans, size: 11 },
          maxRotation: 0,
          autoSkipPadding: 16,
          padding: 4,
        },
      },
      y: {
        grid: { color: gridColor, drawTicks: false, lineWidth: 1 },
        border: { display: false },
        ticks: {
          color: t.fgSubtle,
          font: { family: t.fontMono, size: 11 },
          padding: 8,
          maxTicksLimit: 5,
        },
      },
    },
    animation: {
      duration: 800,
      easing: "easeOutQuart",
    },
  };
}

function buildPalette(t: ChartTokens): string[] {
  // Focal series uses the brand's data-ink colour (Atlantic on Stabiliti),
  // not the CTA colour. Subsequent series stay muted — no rainbow.
  return [t.chartFocal, t.fgMuted, t.info, t.success, t.warning, t.danger];
}
